import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { apiClient } from "../../../../api/httpClient";
import { toast } from "react-toastify";
import debounce from "lodash.debounce";
import { EMAIL_DEBOUNCE_TIMEOUT, is_prod } from "../../../../globals/utils/constants";
import { extractErrorMessage } from "../../../../globals/utils/error-handler";
import { buildFullName } from "../../../../globals/utils/nameUtils";
import { createSupportRequest } from "../../../../services/supportRequestService";
import logger from "../../../../globals/utils/logger";
import { scrollToTop } from "../../../../features/bonds/shared/formHelpers";
import { formatSSN } from "../../../../components/guardian/principalHelpers";
import { toHistoricEffectiveDateIso } from "./historicPayloadBuilders";
import {
  navigateToRoleHome,
  restoreResumedBondPayload,
  storeBondFlashToast,
} from "./historicBondSharedHelpers";

function useBlanketBondCreateHistoric() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isGuestBlanketRequest, setIsGuestBlanketRequest] = useState(
    (location.pathname || "").startsWith("/vermost-blanket-bond-request-guest")
  );
  // Keep for Save/Resume payload compatibility (even though this is now a single-page form)
  const [activeStep, setActiveStep] = useState(0);
  const {
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      obligeeId: null,
    },
  });
  const residencyStatus = watch("residencyStatus");
  const [otherResidency, setOtherResidency] = useState("");
  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [state, setState] = useState(null);
  const [allCounties, setAllCounties] = useState([]);
  const [lawfirm, setLawfirm] = useState(null);
  const [profGuardianRequestor, setProfGuardianRequestor] = useState(null);
  const [obligees, setObligees] = useState([]);
  const [loadingObligees, setLoadingObligees] = useState(false);
  const [openObligeeDialog, setOpenObligeeDialog] = useState(false);
  const lawfirmState = useSelector((state) => state.lawfirm);
  const [newObligee, setNewObligee] = useState({
    name: "",
    street: "",
    city: "",
    state: "",
    zip: "",
  });

  //Redux State
  const [loadingCounties, setLoadingCounties] = useState(false);
  const userdetails = useSelector((state) => state.auth.user);
  const { role } = useSelector((state) => state.auth);
  const pgId = useSelector((state) => state.pg.pgId);
  const [isLoading, setIsLoading] = useState(false);

  // Email existence state
  const [emailExists, setEmailExists] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const emailDebounceRef = useRef();

  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const [failedBondType, setFailedBondType] = useState("");

  // Optional linking to an existing Professional Guardian (historic bonds)
  const [professionalGuardians, setProfessionalGuardians] = useState([]);
  const [loadingGuardians, setLoadingGuardians] = useState(false);

  // Instant Hydration from resumedBondPayload
  useEffect(() => {
    restoreResumedBondPayload({
      reset,
      setActiveStep,
      logContext: "useBlanketBondCreateHistoric",
    });
  }, [reset]);

  // Note: historic blanket flow doesn't support bond-cache saving (no Save button)

  // Debounced function to check email existence
  if (!emailDebounceRef.current) {
    emailDebounceRef.current = debounce(async (email, setValue) => {
      if (!email) {
        setEmailExists(false);
        setEmailChecking(false);
        setEmailDialogOpen(false);
        return;
      }
      setEmailChecking(true);
      try {
        await apiClient("/email-exists", "POST", { email });
        setEmailExists(false);
      } catch (e) {
        if (e?.detail === "Email already exists") {
          setEmailDialogOpen(true);
          setEmailExists(true);
        } else {
          setEmailExists(false);
        }
      } finally {
        setEmailChecking(false);
      }
    }, EMAIL_DEBOUNCE_TIMEOUT);
  }

  // Dialog handlers
  const handleDialogClose = () => {
    setEmailDialogOpen(false);
  };
  const handleDialogContinue = () => {
    setEmailDialogOpen(false);
    setEmailExists(false);
    // Keep the email as is, allow user to proceed
  };
  const handleDialogCancel = () => {
    setEmailDialogOpen(false);
    setValue("email", "");
    setEmailExists(false);
  };

  // Fetch cities, states, and obligees
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const [cities, states, obligeesRes] = await Promise.all([
          apiClient("/fields/city/", "GET"),
          apiClient("/fields/states/", "GET"),
          apiClient("/obligee-list-create/"),
        ]);

        setAllCities(cities);
        setAllStates(states);
        setObligees(obligeesRes);

        // Set default obligee if exists
        const defaultObligee = obligeesRes.find(
          (o) => o.obligee_name === "Governor of Florida (Ron Desantis)"
        );
        if (defaultObligee) {
          setValue("obligeeId", defaultObligee.obligee_id);
        }

        scrollToTop();
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Failed to load data");
      }
    };

    loadDropdownData();

    const savedState = localStorage.getItem("selectedState");
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      setState(parsedState);
    }
  }, [setValue]);

  // Fetch counties when state changes
  useEffect(() => {
    const fetchCounties = async () => {
      if (state?.id) {
        setLoadingCounties(true);
        try {
          const countyResponse = await apiClient(
            `/counties-by-state/${state.id}/`,
            "GET"
          );
          const formattedCounties = countyResponse.map(
            (county) => county.bond_county
          );
          setAllCounties(formattedCounties);
        } catch (error) {
          console.error("Error fetching counties:", error);
        } finally {
          setLoadingCounties(false);
        }
      }
    };
    fetchCounties();
  }, [state]);

  // Fetch professional guardians for optional linking (search by AMS # / name / email)
  useEffect(() => {
    const fetchGuardians = async () => {
      setLoadingGuardians(true);
      try {
        const res = await apiClient("/professional-guardians-linking", "GET");
        const guardians = Array.isArray(res?.data) ? res.data : [];
        setProfessionalGuardians(
          guardians.map((g) => {
            const namePart = g.full_name || "Unknown";
            const amsPart = g.ams_customer_number
              ? ` (AMS: ${g.ams_customer_number})`
              : "";
            const emailPart = g.email_address ? ` — ${g.email_address}` : "";
            return {
              pg_id: g.pg_id,
              full_name: g.full_name || "",
              ams_customer_number: g.ams_customer_number || "",
              email_address: g.email_address || "",
              label: namePart + amsPart + emailPart,
            };
          })
        );
      } catch (err) {
        logger.debug("useBlanketBondCreateHistoric", "professional guardians fetch failed", { err });
        setProfessionalGuardians([]);
      } finally {
        setLoadingGuardians(false);
      }
    };

    fetchGuardians();
  }, []);

  // Handle law firm user case
  useEffect(() => {
    const fetchLawFirm = async () => {
      if (role === "law_firm_user" || role === "law_firm_admin") {
        const lawfirm_id = lawfirmState.lawfirmId;
        setLawfirm(lawfirm_id);
      }
    };
    fetchLawFirm();
  }, [role, lawfirmState.lawfirmId]);

  // Handle professional guardian case
  useEffect(() => {
    if (role === null) {
      setProfGuardianRequestor(pgId);
    }
  }, [role, pgId]);

  const handleOtherResidencyChange = (e) => {
    const value = e.target.value;
    setOtherResidency(value);
    setValue("residencyStatus", value);
  };

  const handleGoogleCallback = (address, city, state, zipCode) => {
    setValue("address", address);
    setValue("city", city);
    setValue("state", state);
    setValue("zipCode", zipCode);
  };

  const handleObligeeGoogleCallback = (address, city, state, zipCode) => {
    setNewObligee((prev) => ({
      ...prev,
      street: address,
      city:
        allCities.find((c) => c.toLowerCase() === city.toLowerCase()) || city,
      state:
        allStates.find((s) => s.toLowerCase() === state.toLowerCase()) || state,
      zip: zipCode,
    }));
  };

  useEffect(() => {
    const path = location.pathname || "";
    setIsGuestBlanketRequest(path.startsWith("/vermost-blanket-bond-request-guest"));
  }, [location.pathname]);

  const handleAddObligee = async () => {
    try {
      setLoadingObligees(true);
      const response = await apiClient("/obligee-list-create/", "POST", {
        obligee_name: newObligee.name,
        obligee_address: newObligee.street,
        obligee_city: newObligee.city,
        obligee_state: newObligee.state,
        obligee_zip: newObligee.zip,
      });

      setObligees((prev) => [...prev, response]);
      setValue("obligeeId", response.obligee_id);
      setOpenObligeeDialog(false);
      setNewObligee({ name: "", street: "", city: "", state: "", zip: "" });
      toast.success("Obligee added successfully!");
    } catch (error) {
      console.error("Error adding obligee:", error);
      toast.error("Failed to add obligee");
    } finally {
      setLoadingObligees(false);
    }
  };

  const calculatePremium = (bondTerm) => {
    switch (bondTerm) {
      case "1 year Bond":
        return 175.0;
      case "2 year Bond":
        return 306.0;
      case "3 year Bond":
        return 437.0;
      default:
        return 175.0;
    }
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    let payload = {};
    try {
      const raw = data?.effectiveDate;
      const effectiveDateIso = toHistoricEffectiveDateIso(raw);

      payload = {
        bond: {
          bond_carrier: is_prod ? 7 : 4,
          bond_type: 5,
          bond_sub_category: data.bondTerm,
          bond_amount: 50000.0,
          premium: calculatePremium(data.bondTerm),
          state: 6,
          county: data.county,
          is_esignatures: data.is_esignatures === "Yes",
          is_guest: isGuestBlanketRequest,
          // Historic bonds are imported as approved
          status: "Approved",
          name: buildFullName(data.firstName, data.middleName, data.lastName),
          law_firm: lawfirm,
          requester_id: isGuestBlanketRequest ? null : userdetails.id,
          managing_lawfirm: lawfirmState.lawfirmId,
          requester_name: isGuestBlanketRequest ? "Guest" : `${userdetails.first_name} ${userdetails.last_name}`,
          created_at: effectiveDateIso,
          updated_at: effectiveDateIso,
          obligee: data.obligeeId,
        },
        blanketbond: {
          first_name: data.firstName,
          middle_name: data.middleName,
          last_name: data.lastName,
          preferred_name: data.preferredName,
          street_address_line_1: data.address,
          street_address_line_2: "",
          city: data.city,
          state: data.state,
          zip: data.zipCode,
          cell_phone: data.cell_phone,
          should_we_text: data.mayWeText === "Yes",
          email: data.email,
          dob: data.dob || null,
          date_of_expiration: data.doe || null,
          ssn: data.ssn,
          is_us_citizen: data.usCitizen === "Yes",
          residency_status: data.residencyStatus,
          bankrupcy: data.bankrupt === "Yes",
          bankrupcy_explanation: data.bankruptExplanation,
          judgements: data.lawsuits === "Yes",
          judgements_explanation: data.lawsuitsExplanation,
          is_decline: data.declinedSurety === "Yes",
          declined_explanation: data.suretyExplanation,
          authorization_underwriting: data.authorizationAgreement,
          is_credit_report: data.authorizationAgreement,
          is_replace: data.replacingBond === "Yes",
          surety_name_reason: data.bondReplacementReason,
          bond_term: data.bondTerm,
          pg_id: data?.selectedProfessionalGuardian?.pg_id || profGuardianRequestor,
          created_at: effectiveDateIso,
          updated_at: effectiveDateIso,
        },
        // Historic endpoint expects bond number assignment at the top-level (bondno_id)
        bond_number: data?.bond_number?.bondno_id ?? null,
        // Used to mark the bond number as used at a historic point in time
        updated_at: effectiveDateIso,
      };

      const response = await apiClient("/create-blanket-bond-historic/", "POST", payload);
      toast.success("Bond created successfully!");
      storeBondFlashToast(response, `/vermost-blanket-bond-update/{bondId}`);
      setIsLoading(false);
      reset();
      setActiveStep(0);
      navigateToRoleHome(navigate, role);
    } catch (error) {
      const message = extractErrorMessage(error);
      console.error("Error submitting form:", error);

      setSubmissionError(message);
      setFailedBondType("Blanket");
      setSupportModalOpen(true);

      setIsLoading(false);
    }
  };

  const handleSupportRequestSubmit = async (description) => {
    try {
      const values = getValues();
      const hydrationPayload = {
        activeStep,
        state: values,
      };

      await createSupportRequest({
        request_body: JSON.stringify(hydrationPayload),
        bond_type: failedBondType,
        name: buildFullName(values.firstName, values.middleName, values.lastName) || "Unknown",
        user_comment: description
      });
      toast.success("Support request created successfully.");
      setSupportModalOpen(false);
      navigateToRoleHome(navigate, role);
    } catch (error) {
      console.error("Failed to create support request", error);
      toast.error("Failed to submit support request.");
    }
  };

  return {
    handleSubmit,
    control,
    watch,
    setValue,
    errors,
    isGuestBlanketRequest,
    isLoading,
    supportModalOpen,
    setSupportModalOpen,
    handleSupportRequestSubmit,
    submissionError,
    onSubmit,
    residencyStatus,
    otherResidency,
    allCities,
    allStates,
    allCounties,
    loadingCounties,
    obligees,
    loadingObligees,
    openObligeeDialog,
    setOpenObligeeDialog,
    newObligee,
    setNewObligee,
    emailExists,
    emailChecking,
    emailDialogOpen,
    emailDebounceRef,
    handleDialogClose,
    handleDialogContinue,
    handleDialogCancel,
    professionalGuardians,
    loadingGuardians,
    handleOtherResidencyChange,
    formatSSN,
    handleGoogleCallback,
    handleObligeeGoogleCallback,
    handleAddObligee,
  };
}

export default useBlanketBondCreateHistoric;
